import { toExternalHref } from './external-href';

/**
 * Neutralise unsafe links in a rendered document/email preview before it goes into the live DOM.
 * Keeps in-document bookmarks (`#…`); for every other `<a href>` it rewrites http(s) links to the
 * normalised href opened in a new tab with `rel="noopener noreferrer"`, and removes the href
 * (leaving the link text) from relative paths and anything `toExternalHref` rejects — `javascript:`,
 * `data:`, `file:`, `vbscript:`, `mailto:`, etc. Mutates `root` in place.
 */
export function sanitizePreviewLinks(root: ParentNode): void {
  for (const anchor of Array.from(root.querySelectorAll('a[href]'))) {
    const raw = anchor.getAttribute('href') ?? '';
    if (raw.startsWith('#')) continue;
    const safe = isAbsoluteUrl(raw) ? toExternalHref(raw) : null;
    if (!safe) {
      anchor.removeAttribute('href');
      continue;
    }
    anchor.setAttribute('href', safe);
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * `URL.canParse(raw)` without `URL.canParse`, which Safari and iOS WebViews only have from 17; the mobile app
 * installs on iOS 15, where calling it throws and the whole preview fails.
 */
function isAbsoluteUrl(raw: string): boolean {
  try {
    return new URL(raw).protocol !== '';
  } catch {
    return false;
  }
}
